import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { myTeamsApi, type LineupWire } from '../api-client.js';

/**
 * The read-only public view at /u/:username/teams — what hbrennan.com
 * links to, and what a visitor sees.
 *
 * Unauthenticated: the API serves this without a token, and the page
 * never asks for one. An unknown user and an unpublished lineup both come
 * back as 404 by design, so this renders the same "nothing here" for
 * either rather than distinguishing them and leaking which it was.
 */
export default function PublicTeamsPage() {
  const { username = '' } = useParams();
  const [data, setData] = useState<{
    user: { username: string; displayName: string };
    lineups: LineupWire[];
  } | null>(null);
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setMissing(false);
    setData(null);
    myTeamsApi
      .published(username)
      .then((result) => !cancelled && setData(result))
      .catch(() => !cancelled && setMissing(true));
    return () => {
      cancelled = true;
    };
  }, [username]);

  if (missing) {
    return (
      <div className="ec-surface m-4 p-4 text-sm">
        <p className="font-display uppercase tracking-wide text-ec-face">Nothing published here</p>
        <p className="mt-2 text-ec-bone-dim">
          {username} has no published teams.
        </p>
      </div>
    );
  }

  if (!data) return <p className="p-4 text-sm text-ec-bone-dim">Loading…</p>;

  return (
    <div className="flex flex-col gap-4 p-4">
      <header>
        <h1 className="font-display text-lg font-medium uppercase tracking-wide text-ec-face">
          {data.user.displayName}&rsquo;s Teams
        </h1>
        <p className="font-mono text-xs text-ec-bone-dim">@{data.user.username}</p>
      </header>

      {data.lineups.map((lineup) => (
        <article key={lineup.slug ?? `${lineup.sport.slug}-${lineup.era.slug}`} className="ec-surface p-4">
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <h2 className="font-display text-sm uppercase tracking-wide text-ec-face">
              {lineup.title ?? `${lineup.sport.name} · ${lineup.format.name} · ${lineup.era.name}`}
            </h2>
            <span className="font-mono text-xs text-ec-bone-dim">
              {lineup.sport.name} · {lineup.format.name} · {lineup.era.name}
            </span>
          </div>

          {lineup.notes && <p className="mt-2 text-sm text-ec-bone-dim">{lineup.notes}</p>}

          <ol className="mt-3 flex flex-col gap-1">
            {lineup.slots.map((slot) => (
              <li key={slot.slotNumber} className="flex items-baseline gap-2 text-sm">
                <span className="w-6 shrink-0 font-mono text-xs text-ec-bone-dim">
                  {slot.slotNumber}
                </span>
                <span className="text-ec-face">{slot.player.knownAs ?? slot.player.fullName}</span>
                {slot.roleFlags.captain && <Chip label="C" />}
                {slot.roleFlags.viceCaptain && <Chip label="VC" />}
                {slot.roleFlags.wicketKeeper && <Chip label="WK" />}
                {slot.position && (
                  <span className="text-xs text-ec-bone-dim">{slot.position.name}</span>
                )}
              </li>
            ))}
          </ol>
        </article>
      ))}
    </div>
  );
}

function Chip({ label }: { label: string }) {
  return (
    <span className="shrink-0 rounded-sm border border-ec-brass/50 bg-ec-ground px-1.5 py-0.5 font-display text-[10px] uppercase tracking-wide text-ec-brass">
      {label}
    </span>
  );
}
